import   React               , { useState, useRef, useEffect }  from 'react';
import   ReactDOM                             from 'react-dom';

import   Box                                  from '../../widget/windows/Box';
import   Trigger                              from '../../widget/actuators/Trigger';
import   RecipientChipInput                   from '../../widget/inputs/RecipientChipInput';
import   AddressBookModal                     from './AddressBookModal';

import { sendWebTvInvitationEmailToMany     ,
         sendWebTvInvitationWhatsApp        }  from '../../../backendServices/liveStreamManagment/api_remoteDevices';
import { Recipient                         ,
         recipientKey                       ,
         recipientDisplay                   }  from '../../../dataTypes/adminManagment/types_addressBook';
import { isValidRecipient                  }  from '../../../utils/utils_recipients';

import   styles                                from './SendChannelUrlModal.module.scss';

// ─────────────────────────────────────────────────────────────────────────────

// Box.window_close_button dépasse de 15px au-dessus et 15px à droite de sa propre Box.
// Les deux Box étant centrées au même point écran (position:fixed + translate(-50%,-50%)),
// la Box du carnet d'adresses doit dépasser cette Box de 2×15px en largeur/hauteur pour
// recouvrir intégralement la croix de fermeture de cette dernière.
const CROSS_OVERFLOW = 15;

interface SendChannelUrlModalProps {
  channelName : string;
  publicUrl   : string;
  userId      : number;
  onClose     : () => void;
}

const SendChannelUrlModal = ({ channelName, publicUrl, userId, onClose }: SendChannelUrlModalProps) => {

  const [ recipients     , setRecipients     ] = useState<Recipient[]>([]);
  const [ addressBookOpen, setAddressBookOpen] = useState(false);
  const [ sending        , setSending        ] = useState(false);
  const [ error          , setError          ] = useState<string | null>(null);
  const [ sentTo         , setSentTo         ] = useState<string[]>([]);
  const [ success        , setSuccess        ] = useState(false);

  // ── Taille de cette Box, mesurée en direct, pour dimensionner celle du carnet
  //    d'adresses de façon à ce qu'elle la recouvre entièrement (croix incluse) ──
  const boxRef = useRef<HTMLDivElement>(null);
  const [ coveringSize, setCoveringSize ] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setCoveringSize({
        width : width  + 2 * CROSS_OVERFLOW,
        height: height + 2 * CROSS_OVERFLOW,
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ─────────────────────────────────────────────────────────────────────────

  // L'adresse de la chaîne part par mail ou par WhatsApp — le carnet est donc
  // ouvert sans restriction de canal, et le dédoublonnage porte sur le couple
  // canal + coordonnée : la même personne peut figurer deux fois, une par
  // canal, et ce sont bien deux envois.
  const handleAddressBookSelect = (picked: Recipient[]) => {
    setRecipients(prev => {
      const connus = new Set(prev.map(recipientKey));
      return [...prev, ...picked.filter(r => {
        const cle = recipientKey(r);
        if (connus.has(cle)) { return false; }
        connus.add(cle);
        return true;
      })];
    });
  };

  /**
   * Le mail accepte une liste en un seul appel ; WhatsApp répond par
   * destinataire (Meta peut refuser), donc un appel chacun, en parallèle —
   * même partage de responsabilité que ProductionControlRoom_RemoteDevicesPanel.
   */
  const handleSend = async () => {
    const valides = recipients.filter(isValidRecipient);
    if (valides.length === 0) return;

    setSending(true);
    setError(null);

    const emails    = valides.filter(r => r.channel === 'email');
    const whatsapps = valides.filter(r => r.channel === 'whatsapp');

    const failures: string[] = [];
    const reussis : string[] = [];

    const envoisWhatsApp = whatsapps.map(async recipient => {
      const affiche = recipientDisplay(recipient);
      try {
        await sendWebTvInvitationWhatsApp({ recipientWhatsapp: recipient.value, channelName, webTvUrl: publicUrl });
        reussis.push(affiche);
      } catch (err: any) {
        failures.push(`${affiche} (${err.message})`);
      }
    });

    const envoiEmail = emails.length > 0
      ? sendWebTvInvitationEmailToMany({ recipientEmails: emails.map(r => r.value), channelName, webTvUrl: publicUrl })
          .then(() => { reussis.push(...emails.map(recipientDisplay)); })
          .catch((err: any) => { failures.push(...emails.map(r => `${recipientDisplay(r)} (${err.message})`)); })
      : Promise.resolve();

    await Promise.all([envoiEmail, ...envoisWhatsApp]);

    setSending(false);

    if (reussis.length === 0) {
      setError(failures.join(', '));
      return;
    }

    setSentTo(reussis);
    setError(failures.length > 0 ? `Échec pour : ${failures.join(', ')}` : null);
    setSuccess(true);
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <Box ref={boxRef} onClose={onClose} width="480px" marginTop="0px" contentPadding="1.5rem">

        <p className={styles.title}>Envoyer l'adresse de la chaîne</p>

        {!success ? (
          <>
            <div className={styles.summary}>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Chaîne :</span>
                <span className={styles.summaryValue}>{channelName}</span>
              </div>
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>URL publique :</span>
                <span className={styles.summaryUrl}>{publicUrl}</span>
              </div>
            </div>

            <label className={styles.inputLabel}>
              Destinataires — Entrée, virgule ou espace pour valider
            </label>
            <div className={styles.chipWrapper}>
              <div style={{ flex: 1 }}>
                <RecipientChipInput value={recipients} onChange={setRecipients}/>
              </div>
              <Trigger
                iconName   = "bookUser"
                iconWidth  = {30}
                iconHeight = {30}
                onClick    = {() => setAddressBookOpen(true)}
              />
            </div>

            {error && <p className={styles.error}>{error}</p>}

            <div className={styles.actions}>
              <Trigger name="Annuler" onClick={onClose} variant="tailwind2"/>
              <Trigger
                name                       = {sending ? 'Envoi…' : 'Envoyer'}
                onClick                    = {handleSend}
                variant                    = "tailwind2"
                unavailable                = {sending || recipients.filter(isValidRecipient).length === 0}
                buttonForegroundColor      = {recipients.length > 0 ? '#FFFFFF' : undefined}
                buttonBackgroundColor      = {recipients.length > 0 ? '#059669' : undefined}
                buttonForegroundHoverColor = {recipients.length > 0 ? '#FFFFFF' : undefined}
                buttonBackgroundHoverColor = {recipients.length > 0 ? '#047857' : undefined}
              />
            </div>
          </>
        ) : (
          <div className={styles.success}>
            <p className={styles.successTitle}>Envoyé</p>
            <p className={styles.successText}>
              L'URL de la chaîne <strong>{channelName}</strong> a été transmise à <strong>{sentTo.join(', ')}</strong>.
            </p>
            {error && <p className={styles.error}>{error}</p>}
            <Trigger name="Fermer" onClick={onClose} variant="tailwind2"/>
          </div>
        )}

      </Box>

      {addressBookOpen && ReactDOM.createPortal(
        <AddressBookModal
          userId             = {userId}
          allowedChannels    = {['email', 'whatsapp']}
          onClose            = {() => setAddressBookOpen(false)}
          onSelectRecipients = {handleAddressBookSelect}
          minWidth           = {coveringSize ? `${coveringSize.width}px`  : undefined}
          minHeight          = {coveringSize ? `${coveringSize.height}px` : undefined}
        />,
        document.body
      )}
    </>
  );
};

export default SendChannelUrlModal;
